import React, { useEffect, useState } from "react";
import { Building2, CalendarDays, Plus, Trash2, Users, X, Globe, Briefcase, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/apiClient";
import AdminShell from "@/components/AdminShell";
import SalonAdmin from "@/pages/SalonAdmin";
import BusinessAdmin from "@/pages/BusinessAdmin";
import WebsiteCMS from "@/pages/admin/WebsiteCMS";
import WeekGrid, { mondayOf, isoDate } from "@/components/WeekGrid";
import MonthGrid, { monthOf } from "@/components/MonthGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = [
  { key: "businesses", label: "Businesses", icon: Briefcase },
  { key: "website", label: "Website", icon: Globe },
];

const DAY_LABELS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const DAYKEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const emptyPattern = () => ({ mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] });
const normalize = (wp) => ({ ...emptyPattern(), ...(wp || {}) });

export default function SuperAdmin() {
  const [tab, setTab] = useState("businesses");
  const [manageTenant, setManageTenant] = useState(null);
  const [manageBusiness, setManageBusiness] = useState(null);
  if (manageBusiness) return <BusinessAdmin actingBusiness={manageBusiness} onExit={() => setManageBusiness(null)} />;
  if (manageTenant) return <SalonAdmin actingTenant={manageTenant} onExit={() => setManageTenant(null)} />;
  return (
    <AdminShell title="Platform Admin" subtitle="Central control across all businesses & salons" tabs={TABS} active={tab} onTab={setTab}>
      {tab === "businesses" && <Businesses onManage={setManageBusiness} />}
      {tab === "website" && <WebsiteCMS />}
    </AdminShell>
  );
}

function Tenants({ onManage }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", slug: "", admin_email: "", admin_password: "", phone: "", address: "", deposit_amount: "", total_price: "" });
  const load = () => api.get("/tenants").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const payload = { ...f,
        deposit_amount: f.deposit_amount ? parseFloat(f.deposit_amount) : null,
        total_price: f.total_price ? parseFloat(f.total_price) : null };
      await api.post("/tenants", payload);
      toast.success("Salon created");
      setOpen(false); setF({ name: "", slug: "", admin_email: "", admin_password: "", phone: "", address: "", deposit_amount: "", total_price: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (t) => {
    await api.put(`/tenants/${t.id}`, { status: t.status === "active" ? "suspended" : "active" });
    load();
  };

  return (
    <div data-testid="tenants-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-[#787672]">{list.length} salons onboarded</p>
        <Button data-testid="add-tenant-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] active:scale-95 transition-transform">
          <Plus className="w-4 h-4 mr-1" /> New salon
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((t) => (
          <Card key={t.id} className="p-5 border-[#E8E6E1] shadow-none hover:-translate-y-1 transition-transform">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-serif text-lg font-bold">{t.name}</h3>
                <a href={`/book/${t.slug}`} target="_blank" rel="noreferrer" className="text-xs" style={{ color: "#9A8C73" }}>/book/{t.slug}</a>
              </div>
              <Badge variant={t.status === "active" ? "default" : "secondary"}>{t.status}</Badge>
            </div>
            <p className="text-sm text-[#787672] mt-3">{t.booking_count} bookings · £{t.deposit_amount} deposit</p>
            <div className="flex gap-2 mt-4">
              <Button data-testid={`manage-tenant-${t.slug}`} onClick={() => onManage(t)} size="sm" className="rounded-full flex-1 bg-[#2C3531] hover:bg-[#1e2422]">Manage</Button>
              <Button data-testid={`toggle-tenant-${t.slug}`} onClick={() => toggle(t)} variant="outline" size="sm" className="rounded-full flex-1">
                {t.status === "active" ? "Suspend" : "Reactivate"}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tenant-dialog">
          <DialogHeader><DialogTitle className="font-serif">Onboard new salon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Salon name</Label><Input data-testid="tenant-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} /></div>
            <div><Label>Slug</Label><Input data-testid="tenant-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Admin email</Label><Input data-testid="tenant-admin-email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} /></div>
              <div><Label>Admin password</Label><Input data-testid="tenant-admin-pw" value={f.admin_password} onChange={(e) => setF({ ...f, admin_password: e.target.value })} /></div>
            </div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Deposit (£)</Label><Input data-testid="tenant-deposit" type="number" value={f.deposit_amount} onChange={(e) => setF({ ...f, deposit_amount: e.target.value })} placeholder="25" /></div>
              <div><Label>Total price (£)</Label><Input data-testid="tenant-total-price" type="number" value={f.total_price} onChange={(e) => setF({ ...f, total_price: e.target.value })} placeholder="99" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="tenant-create-submit" onClick={create} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">Create salon</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Matrix() {
  const [view, setView] = useState("week");
  const [wkOffset, setWkOffset] = useState(0);
  const [moOffset, setMoOffset] = useState(0);
  const monday = mondayOf(wkOffset);
  const monthDate = monthOf(moOffset);
  let start, end;
  if (view === "week") {
    start = isoDate(monday);
    end = isoDate(new Date(monday.getTime() + 6 * 864e5));
  } else {
    const rs = new Date(monthDate); rs.setDate(rs.getDate() - 7);
    const re = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0); re.setDate(re.getDate() + 7);
    start = isoDate(rs); end = isoDate(re);
  }
  const [rows, setRows] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [slots, setSlots] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [staffMap, setStaffMap] = useState({});
  const [staff, setStaff] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => {
    api.get("/global/bookings", { params: { start, end } }).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/staff-blocks").then((r) => setBlocks(r.data)).catch(() => setBlocks([]));
  };
  useEffect(() => {
    api.get("/admin/combined-availability").then((r) => {
      const all = Array.from(new Set(Object.values(r.data).flat())).sort();
      setSlots(all.map((t) => ({ id: t, start_time: t })));
    }).catch(() => {});
    api.get("/admin/business/salons").then((r) => setTenants(r.data)).catch(() => setTenants([]));
    api.get("/admin/staff").then((r) => setStaffMap(Object.fromEntries(r.data.map((s) => [s.id, s.name])))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [view, wkOffset, moOffset]);

  const openTransfer = async (b) => {
    setSel(b);
    const r = await api.get("/global/staff");
    setStaff(r.data);
  };
  const transfer = async (staff_id) => {
    try { await api.put(`/global/bookings/${sel.id}/transfer`, { staff_id }); toast.success("Booking transferred"); setSel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const renderCell = (ds, slotId) => {
    const cellBookings = rows.filter((b) => b.date === ds && b.slot_id === slotId);
    const cellBlocks = blocks.filter((b) => b.date === ds && (b.slot_id === slotId || b.slot_id === null));
    return (
      <>
        {cellBookings.map((b) => (
          <button key={b.id} onClick={() => openTransfer(b)} data-testid={`matrix-booking-${b.ref}`}
            className="w-full text-left text-[11px] rounded-lg px-2 py-1 text-white truncate hover:opacity-90"
            style={{ background: b.tenant_color || "#2C3531" }} title={`${b.customer_name} · ${b.tenant_name} · ${b.staff_name}`}>
            {b.customer_name.split(" ")[0]} · {b.tenant_name}
          </button>
        ))}
        {cellBlocks.map((bl) => (
          <div key={bl.id} className="text-[11px] rounded-lg px-2 py-1 bg-[#E8E6E1] text-[#787672] truncate" title="Technician time-off">
            {(staffMap[bl.staff_id] || "Tech").split(" ")[0]} off
          </div>
        ))}
      </>
    );
  };

  const renderDay = (ds) => {
    const db = rows.filter((b) => b.date === ds).sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || ""));
    const bl = blocks.filter((b) => b.date === ds);
    return (
      <>
        {db.slice(0, 3).map((b) => (
          <button key={b.id} onClick={() => openTransfer(b)} data-testid={`matrix-booking-${b.ref}`}
            className="w-full text-left text-[10px] leading-tight rounded px-1.5 py-0.5 text-white truncate hover:opacity-90"
            style={{ background: b.tenant_color || "#2C3531" }} title={`${b.slot_start} · ${b.customer_name} · ${b.tenant_name} · ${b.staff_name}`}>
            {b.slot_start} {b.customer_name.split(" ")[0]}
          </button>
        ))}
        {db.length > 3 && <div className="text-[10px] text-[#787672] pl-1">+{db.length - 3} more</div>}
        {bl.slice(0, 2).map((x) => (
          <div key={x.id} className="text-[10px] rounded px-1.5 py-0.5 bg-[#E8E6E1] text-[#787672] truncate" title="Technician time-off">{(staffMap[x.staff_id] || "Tech").split(" ")[0]} off</div>
        ))}
      </>
    );
  };

  return (
    <div data-testid="matrix-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[#787672] max-w-2xl">All bookings across salons (color-coded) and technician time-off. Click a booking to reassign it to another technician.</p>
        <div className="inline-flex rounded-full border border-[#E8E6E1] p-1 bg-white shrink-0">
          <button data-testid="matrix-view-week" onClick={() => setView("week")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "week" ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}>Week</button>
          <button data-testid="matrix-view-month" onClick={() => setView("month")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "month" ? "bg-[#2C3531] text-white" : "text-[#787672]"}`}>Month</button>
        </div>
      </div>
      {view === "week" ? (
        <WeekGrid slots={slots} monday={monday} testid="global-calendar"
          onPrev={() => setWkOffset(wkOffset - 1)} onNext={() => setWkOffset(wkOffset + 1)} onToday={() => setWkOffset(0)}
          renderCell={renderCell} />
      ) : (
        <MonthGrid monthDate={monthDate} testid="global-calendar-month"
          onPrev={() => setMoOffset(moOffset - 1)} onNext={() => setMoOffset(moOffset + 1)} onToday={() => setMoOffset(0)}
          renderDay={renderDay} />
      )}
      {tenants.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-4">
          {tenants.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-2 text-xs text-[#787672]">
              <span className="w-3 h-3 rounded-full" style={{ background: t.primary_color }} /> {t.name}
            </span>
          ))}
          <span className="inline-flex items-center gap-2 text-xs text-[#787672]"><span className="w-3 h-3 rounded-full bg-[#E8E6E1]" /> Time-off</span>
        </div>
      )}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">Reassign to technician</DialogTitle></DialogHeader>
          <p className="text-sm text-[#787672]">{sel?.customer_name} — {sel?.tenant_name} · {sel?.date} {sel?.slot_start}</p>
          <div className="space-y-2 mt-2">
            {staff.map((s) => (
              <Button key={s.id} data-testid={`transfer-to-${s.id}`} variant="outline" onClick={() => transfer(s.id)} className="w-full rounded-full justify-start">{s.name}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Businesses({ onManage }) {
  const [list, setList] = useState([]);
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [nb, setNb] = useState({ business_name: "", owner_email: "", contact_name: "", phone: "" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-platform", { confirm: "RESET ALL DATA" });
      toast.success(`Platform reset — ${data.super_admins_remaining} super admin(s) remain`);
      setResetOpen(false); setResetPhrase(""); loadList(); loadApps();
    } catch (e) { toast.error(apiErr(e)); } finally { setResetting(false); }
  };
  const loadList = () => api.get("/admin/businesses").then((r) => setList(r.data)).catch(() => setList([]));
  const loadApps = () => api.get("/admin/business-applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  useEffect(() => { loadList(); loadApps(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/business-applications/${id}/${action}`); toast.success(`Application ${action}d`); loadList(); loadApps(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (b) => {
    try { await api.put(`/admin/businesses/${b.id}`, { status: b.status === "active" ? "suspended" : "active" }); toast.success("Updated"); loadList(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const createBusiness = async () => {
    if (!nb.business_name.trim() || !nb.owner_email.trim()) return toast.error("Business name and owner email are required");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/businesses", nb);
      setOpen(false); setNb({ business_name: "", owner_email: "", contact_name: "", phone: "" });
      setCreated(data); loadList();
      toast.success("Business created");
    } catch (e) { toast.error(apiErr(e)); } finally { setCreating(false); }
  };
  const pending = apps.filter((a) => a.status === "pending");
  const color = { pending: "default", approved: "secondary", rejected: "destructive" };
  const regUrl = `${window.location.origin}/register`;
  return (
    <div className="space-y-8" data-testid="businesses-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Card className="p-5 border-[#E8E6E1] shadow-none bg-[#F7F5F1] flex-1 min-w-[280px]">
          <p className="text-sm text-[#5B5954] leading-relaxed">
            Businesses join the platform by <strong>applying</strong> at <a href={regUrl} target="_blank" rel="noreferrer" className="underline font-semibold">{regUrl}</a>.
            Their applications appear below for you to <strong>approve or reject</strong> — approving one automatically creates the business, its recruitment page and its admin login. Or use <strong>New business</strong> to onboard one directly.
          </p>
        </Card>
        <Button data-testid="new-business-btn" onClick={() => setOpen(true)} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] active:scale-95 transition-transform shrink-0">
          <Plus className="w-4 h-4 mr-1" /> New business
        </Button>
      </div>
      <div>
        <h3 className="font-serif text-xl font-bold mb-3">Business applications {pending.length > 0 && <Badge className="ml-1">{pending.length} pending</Badge>}</h3>
        <div className="max-w-3xl space-y-3">
          {pending.length === 0 && (
            <Card className="p-6 border-dashed border-[#D8D4CC] shadow-none text-center" data-testid="business-apps-empty">
              <p className="text-[#787672]">No applications waiting for review. New business applications submitted at <span className="font-semibold">/register</span> will show up here for approval.</p>
            </Card>
          )}
          {pending.map((a) => (
            <Card key={a.id} className="p-5 border-[#9A8C73]/40 shadow-none bg-[#F7F5F1]" data-testid={`business-app-${a.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <h4 className="font-serif text-lg font-bold">{a.business_name}</h4>
                  <p className="text-sm text-[#787672]">{a.contact_name} · {a.email} · {a.phone}</p>
                  <p className="text-sm text-[#787672]">{a.city} · {a.specialty}</p>
                  {a.message && <p className="text-sm mt-2 italic">"{a.message}"</p>}
                </div>
                <Badge variant={color[a.status]}>{a.status}</Badge>
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`approve-business-${a.id}`} onClick={() => act(a.id, "approve")} className="rounded-full bg-[#3B5341] hover:bg-[#2e4133] flex-1"><Check className="w-4 h-4 mr-1" /> Approve & create business</Button>
                <Button data-testid={`reject-business-${a.id}`} onClick={() => act(a.id, "reject")} variant="outline" className="rounded-full flex-1">Reject</Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-xl font-bold mb-3">Active businesses</h3>
        {list.length === 0 && <p className="text-[#787672]">No businesses yet.</p>}
        <div className="grid md:grid-cols-2 gap-4">
          {list.map((b) => (
            <Card key={b.id} className="p-5 border-[#E8E6E1] shadow-none" data-testid={`business-${b.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-serif text-lg font-bold">{b.name}</p>
                  <p className="text-sm text-[#787672]">/partner/{b.slug} · {b.salon_count} salons · {b.staff_count} technicians</p>
                </div>
                <Badge variant={b.status === "active" ? "default" : "secondary"}>{b.status}</Badge>
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`manage-business-${b.id}`} onClick={() => onManage(b)} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] flex-1">Manage</Button>
                <Button onClick={() => toggle(b)} variant="outline" className="rounded-full">{b.status === "active" ? "Suspend" : "Reactivate"}</Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div className="pt-4 mt-2 border-t border-dashed border-[#E0C9C9]" data-testid="danger-zone">
        <h3 className="font-serif text-lg font-bold text-[#993333]">Danger zone</h3>
        <p className="text-sm text-[#787672] mt-1 mb-3">Permanently delete <strong>all</strong> businesses, salons, bookings and their logins, leaving only super admin accounts. Use this to start a clean slate before live testing. This cannot be undone.</p>
        <Button data-testid="reset-platform-btn" onClick={() => setResetOpen(true)} variant="outline" className="rounded-full text-[#993333] border-[#993333]/40 hover:bg-[#993333]/10">Reset platform data</Button>
      </div>

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setResetPhrase(""); } }}>
        <DialogContent data-testid="reset-platform-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#993333]">Reset all platform data?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#787672]">This deletes every business, salon, booking and application, plus all business/salon logins. Super admin accounts are kept. Type <span className="font-bold">RESET ALL DATA</span> to confirm.</p>
          <Input data-testid="reset-confirm-input" value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} placeholder="RESET ALL DATA" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setResetOpen(false); setResetPhrase(""); }}>Cancel</Button>
            <Button data-testid="reset-confirm-btn" disabled={resetPhrase !== "RESET ALL DATA" || resetting} onClick={doReset} className="rounded-full bg-[#993333] hover:bg-[#7a2828]">{resetting ? "Resetting…" : "Delete everything"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-business-dialog">
          <DialogHeader><DialogTitle className="font-serif">Onboard a new business</DialogTitle></DialogHeader>
          <p className="text-sm text-[#787672] -mt-1">We'll create the business and email the owner a temporary password. They can also sign in with Google using the same email.</p>
          <div className="space-y-3 mt-1">
            <div><Label>Business name</Label><Input data-testid="nb-name" value={nb.business_name} onChange={(e) => setNb({ ...nb, business_name: e.target.value })} placeholder="e.g. Elevate Aesthetics" /></div>
            <div><Label>Owner email (login)</Label><Input data-testid="nb-email" type="email" value={nb.owner_email} onChange={(e) => setNb({ ...nb, owner_email: e.target.value })} placeholder="owner@business.com" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact name</Label><Input data-testid="nb-contact" value={nb.contact_name} onChange={(e) => setNb({ ...nb, contact_name: e.target.value })} placeholder="Optional" /></div>
              <div><Label>Phone</Label><Input data-testid="nb-phone" value={nb.phone} onChange={(e) => setNb({ ...nb, phone: e.target.value })} placeholder="Optional" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="nb-submit" onClick={createBusiness} disabled={creating} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">{creating ? "Creating…" : "Create business"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent data-testid="new-business-result">
          <DialogHeader><DialogTitle className="font-serif">Business created</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-[#5B5954]">The owner has been emailed their login details. Share these credentials if the email doesn't arrive:</p>
            <div className="rounded-xl bg-[#F7F5F1] border border-[#E8E6E1] p-4 space-y-1">
              <p><span className="text-[#787672]">Login email:</span> <b data-testid="result-email">{created?.owner_email}</b></p>
              <p><span className="text-[#787672]">Temporary password:</span> <b data-testid="result-temp-password">{created?.temp_password}</b></p>
              <p><span className="text-[#787672]">Recruitment page:</span> /partner/{created?.slug}</p>
            </div>
            <p className="text-xs text-[#9A8C73]">They can also use “Continue with Google” with this email — no password needed.</p>
          </div>
          <DialogFooter>
            <Button className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]" onClick={() => setCreated(null)} data-testid="result-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export function StaffManager() {
  const [staff, setStaff] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [combined, setCombined] = useState(emptyPattern());
  const [name, setName] = useState("");
  const [selId, setSelId] = useState("");
  const [pattern, setPattern] = useState(emptyPattern());
  const [dayInput, setDayInput] = useState({});
  const [bf, setBf] = useState({ staff_id: "", date: "", time: "all" });
  const [calOffset, setCalOffset] = useState(0);
  const [calBookings, setCalBookings] = useState([]);
  const calMonth = monthOf(calOffset);

  const load = (keepSel) =>
    Promise.all([api.get("/admin/staff"), api.get("/admin/staff-blocks"), api.get("/admin/combined-availability")])
      .then(([s, b, c]) => {
        setStaff(s.data); setBlocks(b.data); setCombined(normalize(c.data));
        if (!keepSel && !selId && s.data[0]) { setSelId(s.data[0].id); setPattern(normalize(s.data[0].weekly_pattern)); }
      });
  useEffect(() => { load(false); }, []);
  useEffect(() => {
    const rs = new Date(calMonth); rs.setDate(rs.getDate() - 7);
    const re = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0); re.setDate(re.getDate() + 7);
    api.get("/global/bookings", { params: { start: isoDate(rs), end: isoDate(re) } }).then((r) => setCalBookings(r.data)).catch(() => {});
  }, [calOffset, staff.length]);

  const selectTech = (s) => { setSelId(s.id); setPattern(normalize(s.weekly_pattern)); };
  const addTech = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/admin/staff", { name });
      setName(""); toast.success("Technician added — now set their weekly availability");
      await load(true); selectTech(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delTech = async (id) => { await api.delete(`/admin/staff/${id}`); if (id === selId) setSelId(""); toast.success("Technician removed"); load(false); };
  const toggleActive = async (s) => { await api.put(`/admin/staff/${s.id}`, { is_active: !s.is_active }); load(true); };

  const addTime = (day) => {
    const v = (dayInput[day] || "").trim();
    if (!/^\d{2}:\d{2}$/.test(v)) return toast.error("Enter a time like 10:00");
    if ((pattern[day] || []).includes(v)) return;
    setPattern({ ...pattern, [day]: [...(pattern[day] || []), v].sort() });
    setDayInput({ ...dayInput, [day]: "" });
  };
  const removeTime = (day, t) => setPattern({ ...pattern, [day]: pattern[day].filter((x) => x !== t) });
  const saveWeekly = async () => {
    if (!selId) return;
    try { await api.put(`/admin/staff/${selId}`, { weekly_pattern: pattern }); toast.success("Weekly availability saved"); load(true); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const allTimes = Array.from(new Set(Object.values(combined).flat())).sort();
  const addBlock = async () => {
    if (!bf.staff_id || !bf.date) return toast.error("Pick technician and date");
    try {
      await api.post("/admin/staff-blocks", { staff_id: bf.staff_id, date: bf.date, slot_id: bf.time === "all" ? null : bf.time });
      toast.success("Time-off added"); setBf({ staff_id: "", date: "", time: "all" }); load(true);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delBlock = async (id) => { await api.delete(`/admin/staff-blocks/${id}`); load(true); };
  const nameOf = (id) => staff.find((s) => s.id === id)?.name || "?";
  const activeCount = staff.filter((s) => s.is_active).length;
  const selTech = staff.find((s) => s.id === selId);

  const renderTechDay = (ds) => {
    const wk = DAYKEYS[parseISO(ds).getDay()];
    const worksTimes = selTech?.weekly_pattern?.[wk] || [];
    const booked = calBookings.filter((b) => b.staff_id === selId && b.date === ds)
      .sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || ""));
    const dayBlocks = blocks.filter((b) => b.staff_id === selId && b.date === ds);
    const wholeOff = dayBlocks.some((b) => b.slot_id === null || b.slot_id === undefined);
    const offTimes = new Set(dayBlocks.filter((b) => b.slot_id).map((b) => b.slot_id));
    const bookedTimes = new Set(booked.map((b) => b.slot_id));
    const freeCount = wholeOff ? 0 : worksTimes.filter((t) => !offTimes.has(t) && !bookedTimes.has(t)).length;
    return (
      <>
        {booked.slice(0, 3).map((b) => (
          <div key={b.id} className="text-[10px] leading-tight rounded px-1.5 py-0.5 text-white truncate" style={{ background: b.tenant_color || "#2C3531" }} title={`${b.slot_start} · ${b.tenant_name}`}>
            {b.slot_start} {b.tenant_name}
          </div>
        ))}
        {booked.length > 3 && <div className="text-[10px] text-[#787672] pl-1">+{booked.length - 3} more</div>}
        {wholeOff ? (
          <div className="text-[10px] rounded px-1.5 py-0.5 bg-[#E8E6E1] text-[#787672]">Off all day</div>
        ) : offTimes.size > 0 ? (
          <div className="text-[10px] rounded px-1.5 py-0.5 bg-[#E8E6E1] text-[#787672]">{offTimes.size} off</div>
        ) : null}
        {!wholeOff && worksTimes.length > 0 && freeCount > 0 && (
          <div className="text-[10px] rounded px-1.5 py-0.5 bg-[#3B5341]/12 text-[#3B5341]">{freeCount} free</div>
        )}
      </>
    );
  };

  return (
    <div data-testid="staff-manager" className="max-w-5xl">
      <Card className="p-5 border-[#E8E6E1] shadow-none mb-6 bg-[#F7F5F1]">
        <p className="text-sm text-[#5B5954] leading-relaxed">
          These are your <strong>travelling technicians</strong>. Each technician sets the times they work for every day of the week. The <strong>combined</strong> availability of all technicians becomes the pool of bookable times, and salons pick from that pool for their own calendars. You have <strong>{activeCount}</strong> active technician{activeCount === 1 ? "" : "s"}.
        </p>
      </Card>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        <Card className="p-5 border-[#E8E6E1] shadow-none h-fit">
          <h3 className="font-serif text-lg font-bold mb-3">Technicians</h3>
          <div className="flex gap-2 mb-4">
            <Input data-testid="staff-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Technician name" onKeyDown={(e) => e.key === "Enter" && addTech()} />
            <Button data-testid="add-staff-btn" onClick={addTech} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {staff.map((s) => (
              <div key={s.id} onClick={() => selectTech(s)} data-testid={`staff-item-${s.name}`}
                className={`flex items-center justify-between rounded-xl px-4 py-2.5 cursor-pointer transition-colors ${selId === s.id ? "bg-[#2C3531] text-white" : "bg-[#FDFBF7] hover:bg-[#F0EFEB]"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${s.is_active ? "bg-[#4ED8A0]" : "bg-[#C9B8B8]"}`} />
                  <span className={`font-medium truncate ${s.is_active ? "" : "line-through opacity-60"}`}>{s.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleActive(s); }} className={`rounded-full text-xs h-7 px-2 ${selId === s.id ? "text-white hover:bg-white/20" : ""}`} data-testid={`toggle-staff-${s.name}`}>{s.is_active ? "Pause" : "On"}</Button>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); delTech(s.id); }} className="h-7 w-7" data-testid={`delete-staff-${s.name}`}><Trash2 className="w-4 h-4 text-[#E58B8B]" /></Button>
                </div>
              </div>
            ))}
            {staff.length === 0 && <p className="text-sm text-[#787672]">No technicians yet. Add one to start setting availability.</p>}
          </div>
        </Card>

        <Card className="p-5 border-[#E8E6E1] shadow-none" data-testid="weekly-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-bold">{selTech ? `${selTech.name}'s weekly availability` : "Weekly availability"}</h3>
            {selTech && <Button data-testid="save-weekly-btn" onClick={saveWeekly} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">Save availability</Button>}
          </div>
          {!selTech ? <p className="text-sm text-[#787672]">Select a technician on the left to set the times they work each day.</p> : (
            <div className="space-y-2.5">
              {DAY_LABELS.map(([key, label]) => (
                <div key={key} className="flex flex-wrap items-center gap-2 border-b border-[#F0EFEB] pb-2.5" data-testid={`weekly-day-${key}`}>
                  <span className="w-20 text-sm font-semibold shrink-0">{label}</span>
                  <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                    {(pattern[key] || []).length === 0 && <span className="text-xs text-[#B0AEA8] italic">Not working</span>}
                    {(pattern[key] || []).map((t) => (
                      <span key={t} data-testid={`weekly-chip-${key}-${t}`} className="inline-flex items-center gap-1 bg-[#3B5341]/12 text-[#2C3531] rounded-full pl-3 pr-1.5 py-1 text-xs font-semibold">
                        {t}
                        <button onClick={() => removeTime(key, t)} data-testid={`weekly-remove-${key}-${t}`} className="rounded-full hover:bg-[#993333]/15 p-0.5"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Input type="time" data-testid={`weekly-time-input-${key}`} value={dayInput[key] || ""} onChange={(e) => setDayInput({ ...dayInput, [key]: e.target.value })} className="w-28 h-9" />
                    <Button variant="outline" size="icon" className="rounded-full h-9 w-9" data-testid={`weekly-add-${key}`} onClick={() => addTime(key)}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5 border-[#E8E6E1] shadow-none mt-6" data-testid="combined-availability">
        <h3 className="font-serif text-lg font-bold mb-1">Combined bookable times</h3>
        <p className="text-xs text-[#787672] mb-4">The union of every active technician's times — this is what salons can pick from.</p>
        <div className="space-y-2">
          {DAY_LABELS.map(([key, label]) => (
            <div key={key} className="flex flex-wrap items-center gap-2">
              <span className="w-20 text-sm font-semibold shrink-0">{label}</span>
              {(combined[key] || []).length === 0 ? <span className="text-xs text-[#B0AEA8] italic">No availability</span> :
                (combined[key] || []).map((t) => <span key={t} className="bg-[#F0EFEB] text-[#5B5954] rounded-full px-3 py-1 text-xs font-semibold">{t}</span>)}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-[#E8E6E1] shadow-none mt-6 max-w-2xl">
        <h3 className="font-serif text-lg font-bold mb-3">Holidays & one-off time-off</h3>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label>Technician</Label>
            <Select value={bf.staff_id} onValueChange={(v) => setBf({ ...bf, staff_id: v })}>
              <SelectTrigger data-testid="block-staff-select"><SelectValue placeholder="Technician" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" data-testid="block-date" value={bf.date} onChange={(e) => setBf({ ...bf, date: e.target.value })} /></div>
          <div>
            <Label>Time</Label>
            <Select value={bf.time} onValueChange={(v) => setBf({ ...bf, time: v })}>
              <SelectTrigger data-testid="block-time-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Whole day</SelectItem>
                {allTimes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="add-block-btn" onClick={addBlock} variant="outline" className="rounded-full mt-3">Add time-off</Button>
        <div className="space-y-2 mt-4">
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between bg-[#FDFBF7] rounded-xl px-4 py-2.5 text-sm">
              <span>{nameOf(b.staff_id)} · {b.date} · {b.slot_id || "All day"}</span>
              <Button variant="ghost" size="icon" onClick={() => delBlock(b.id)}><Trash2 className="w-4 h-4 text-[#993333]" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-[#E8E6E1] shadow-none mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-serif text-lg font-bold">{selTech ? `${selTech.name}'s live calendar` : "Technician calendar"}</h3>
        </div>
        {selTech ? (
          <MonthGrid monthDate={calMonth} testid="tech-calendar"
            onPrev={() => setCalOffset(calOffset - 1)} onNext={() => setCalOffset(calOffset + 1)} onToday={() => setCalOffset(0)}
            renderDay={renderTechDay} />
        ) : <p className="text-sm text-[#787672]">Select a technician to view their calendar.</p>}
        <div className="flex flex-wrap gap-3 mt-4 text-xs text-[#787672]">
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#3B5341]/40" /> Available</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#2C3531]" /> Booked</span>
          <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#E8E6E1]" /> Time-off</span>
        </div>
      </Card>
    </div>
  );
}
